import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Lock, Mail, ArrowLeft } from 'lucide-react';

const RSS_LOGO = "https://customer-assets.emergentagent.com/job_199c36c2-e07b-4567-84a2-e99d8a5503d6/artifacts/c9vy7sfe_rumpun_silat_sarawak_logo_transparent_1024.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      navigate(user.role === 'RSS_ADMIN' ? '/admin/dashboard' : '/school/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success('Berjaya log masuk');
      navigate(userData.role === 'RSS_ADMIN' ? '/admin/dashboard' : '/school/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email atau kata laluan salah');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif" }}
      className="min-h-screen grid lg:grid-cols-2"
    >
      {/* ── Left Panel — Branding ─────────────────────────── */}
      <div className="hidden lg:flex flex-col bg-[#0B0B0B] relative overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-[#D71920] via-[#FFD21F] to-[#D71920]" />

        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `repeating-linear-gradient(0deg, #fff 0, #fff 1px, transparent 1px, transparent 40px),
              repeating-linear-gradient(90deg, #fff 0, #fff 1px, transparent 1px, transparent 40px)`,
          }}
        />

        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          {/* Logo + Name */}
          <div className="flex items-center gap-4">
            <img src={RSS_LOGO} alt="RSS" className="h-14 w-14 object-contain" />
            <div>
              <div className="text-white font-bold text-base leading-tight tracking-tight">Rumpun Silat</div>
              <div className="text-[#FFD21F] text-xs tracking-widest font-medium leading-tight">SARAWAK</div>
            </div>
          </div>

          {/* Center content */}
          <div>
            <div className="w-16 h-0.5 bg-[#D71920] mb-8 rounded" />
            <h2 className="text-4xl font-extrabold text-white leading-tight mb-6">
              Portal Digital<br />
              <span className="text-[#FFD21F]">Rumpun Silat</span><br />
              Sarawak
            </h2>
            <p className="text-[#888] text-sm leading-relaxed max-w-xs">
              Sistem pengurusan rasmi untuk sekretariat, perguruan dan pentadbir yang berdaftar dengan Rumpun Silat Sarawak.
            </p>
          </div>

          {/* Motto */}
          <div className="border-t border-white/10 pt-8">
            <p className="text-xs font-bold tracking-[0.3em] text-[#D4AF37] uppercase">
              Berseni · Berbudi · Berbakti
            </p>
          </div>
        </div>
      </div>

      {/* ── Right Panel — Form ────────────────────────────── */}
      <div className="flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md">

          {/* Back to home */}
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-[#999] hover:text-[#0B0B0B] mb-10 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft size={13} /> Kembali ke Laman Utama
          </Link>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={RSS_LOGO} alt="RSS" className="h-10 w-10 object-contain" />
            <div>
              <div className="text-[#0B0B0B] font-bold text-sm leading-tight">Rumpun Silat Sarawak</div>
              <div className="text-[#D71920] text-xs tracking-widest">PORTAL AHLI</div>
            </div>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-[#0B0B0B] mb-2" data-testid="login-title">
              Portal Ahli Rumpun Silat Sarawak
            </h1>
            <p className="text-sm text-[#777]">
              Akses untuk sekretariat, perguruan dan pentadbir berdaftar.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-[#0B0B0B] uppercase tracking-wide mb-1.5 block">
                Alamat Emel
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#bbb]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="emel@perguruan.com"
                  className="pl-10 h-11 border-[#E5E5E5] focus:border-[#0B0B0B] text-sm"
                  data-testid="email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold text-[#0B0B0B] uppercase tracking-wide mb-1.5 block">
                Kata Laluan
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#bbb]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="pl-10 h-11 border-[#E5E5E5] focus:border-[#0B0B0B] text-sm"
                  data-testid="password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0B0B0B] hover:bg-[#333] text-white font-semibold text-sm rounded transition-colors mt-2"
              data-testid="login-button"
            >
              {loading ? 'Memproses...' : 'Log Masuk'}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-7">
            <div className="h-px bg-[#E5E5E5] flex-1" />
            <span className="text-xs text-[#bbb]">atau</span>
            <div className="h-px bg-[#E5E5E5] flex-1" />
          </div>

          {/* Register links */}
          <div className="space-y-2.5">
            <Link
              to="/register-school"
              className="block w-full text-center py-2.5 border border-[#E5E5E5] hover:border-[#D71920] hover:text-[#D71920] text-sm font-medium text-[#555] rounded transition-colors"
              data-testid="register-school-link"
            >
              Daftar Perguruan Baru
            </Link>
            <Link
              to="/register-member"
              className="block w-full text-center py-2.5 border border-[#E5E5E5] hover:border-[#D4AF37] hover:text-[#0B0B0B] text-sm font-medium text-[#555] rounded transition-colors"
              data-testid="register-member-link"
            >
              Daftar Keahlian / Pesilat
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
